import _ from 'lodash';

import { CoreEnums } from '@/utils/domain/constants';
import BaseFieldHandler from '../BaseFieldHandler';
import { checkLikertSelection } from '../utils';

const { FormFieldType, FormFieldStringType } = CoreEnums;

export default class InteractiveFieldHandler extends BaseFieldHandler {
  static supportedTypes = [
    FormFieldStringType.LikertScale,
    FormFieldType.LikertScale,
    FormFieldStringType.CheckGroup,
    FormFieldStringType.RadioGroup,
    FormFieldType.CheckGroup,
    FormFieldType.RadioGroup,
    FormFieldStringType.Slider,
    FormFieldType.Slider,
  ];

  handle() {
    const { type } = this.baseConfig;

    const handlers = {
      [FormFieldStringType.LikertScale]: () => this.handleLikertScale(),
      [FormFieldType.LikertScale]: () => this.handleLikertScale(),
      [FormFieldStringType.CheckGroup]: () => this.handleRadioCheckGroup(),
      [FormFieldStringType.RadioGroup]: () => this.handleRadioCheckGroup(),
      [FormFieldType.CheckGroup]: () => this.handleRadioCheckGroup(),
      [FormFieldType.RadioGroup]: () => this.handleRadioCheckGroup(),
      [FormFieldStringType.Slider]: () => this.handleSlider(),
      [FormFieldType.Slider]: () => this.handleSlider(),
    };

    const handler = handlers[type];
    if (!handler) {
      throw new Error(`Unsupported interactive field type: ${type}`);
    }

    return handler();
  }

  handleLikertScale() {
    const config = this.getBaseConfig();
    const { isDetails } = this.fieldState;
    const { questions = [], responses = [], allowMultiple, required } = this.baseConfig;

    const rows = questions.map((k) => ({ id: k, question: k }));
    const columns = responses.map((k) => ({ id: k, response: k }));

    return {
      ...config,
      type: 'likert-scale',
      multipleChoicesEnabled: allowMultiple,
      disabled: isDetails,
      rows,
      columns,
      rules: [
        ...config.rules,
        () => ({
          validator(_, value) {
            return checkLikertSelection(value, rows, { required, allowMultiple });
          },
        }),
      ],
      ...(allowMultiple &&
        config.value && {
          value: {
            items: _.reduce(config.value, (lhs, rhs) => lhs.concat(rhs), []),
          },
        }),
    };
  }

  handleRadioCheckGroup() {
    const config = this.getBaseConfig();
    const {
      type,
      displayText,
      displayName,
      maxSelectionsAllowed,
      minSelectionsRequired,
      separator,
    } = this.baseConfig;

    let { choices } = this.baseConfig;
    if (_.isString(choices)) {
      choices = choices.split(';');
    }

    const isCheckGroup =
      type === FormFieldType.CheckGroup || type === FormFieldStringType.CheckGroup;

    return {
      ...config,
      type: 'radio-group',
      locale: false,
      label: displayText ?? displayName,
      options: choices.map((k) => ({ label: k, value: k })),

      ...(maxSelectionsAllowed > 1 && { maxSelectionsAllowed }),
      ...(minSelectionsRequired > 1 && { minSelectionsRequired }),
      ...(isCheckGroup && {
        type: 'check-group',
        value: _.isString(config.value)
          ? _.split(config.value, separator || ';').filter(Boolean)
          : config.value || [],
      }),
    };
  }

  handleSlider() {
    const config = this.getBaseConfig();
    const { maxValue, minValue, minIntValue, step } = this.baseConfig;
    const min = Math.max(minValue ?? 0, 0);
    // Prefer configured start; never fall back to 0 when min > 0 (that made
    // submits send 0 and fail "must be >= min" validation).
    const initial = minIntValue != null ? minIntValue : min;

    return {
      ...config,
      type: 'slider',
      max: maxValue || 100,
      min,
      defaultValue: initial,
      initialValue: initial,
      wrapperCol: { span: 24 },
      labelCol: { span: 24 },
      step,
    };
  }
}
